import styled from "@emotion/styled";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useIsMobile} from "modules/utils";
import {fetchLeagueUsers, ILeagueUser, removeLeagueUser} from "modules/actions/leagues";
import {
	getCurrentLeague,
	getCurrentUserId,
	getLeagueUsers,
	isLeagueRequested,
} from "modules/selectors";
import {eq, get} from "lodash";
import {useTranslation} from "react-i18next";
import {IconClose} from "components/Icons/IconClose";
import {showPromptModal} from "modules/actions/prompt_modal";
import {
	DisplayName,
	Rank,
} from "components/Leaderboard/leaderboardContent/leaderboards/leaderboardTable/styles";
// import defaultAvatar from "assets/img/avatar_MLB.png";
import {MiddleLoading} from "components/MiddleLoading";

const Wrapper = styled.div`
	width: 100%;
	flex: 1 1 100%;
`;
const Container = styled.section`
	flex: 1 1 100%;
	//padding: 16px 20px 20px;
	gap: 20px;
	background: #292f37;
	border-radius: 8px;
	margin-top: 16px;
	width: 100%;
	overflow: hidden;
	//flex-shrink: 0;

	@media (max-width: 880px) {
		max-width: 100%;
	}
`;
const NoAvailableUsers = styled.div`
	width: 100%;
	font-size: 20px;
	display: flex;
	justify-content: center;
	margin: 15px auto;
`;
const UsersTable = styled.div`
	width: 100%;
`;
const Button = styled.div`
	margin-left: auto;
	position: absolute;
	top: 50%;
	right: 12px;
	padding: 0;
	box-sizing: content-box;
	background: none;
	box-shadow: none;
	border: none;
	transform: translateY(-50%);
	cursor: pointer;

	svg {
		width: 20px;
		height: 20px;
		pointer-events: none;
	}
`;

const HeaderRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	//gap: 20px;
	//padding: 12px 20px;
	box-sizing: border-box;
	background: #292f37;

	gap: 10px;
	padding: 0 15px;
	height: 52px;
	margin-top: 1px;
	bottom: 0;
	position: relative;
`;
const Row = styled(HeaderRow)`
	background: linear-gradient(90deg, #42474d 0%, #282f38 100%);

	&.commissioner {
		background: linear-gradient(90deg, #232323 0%, #151515 100%);
	}
`;
const Cell = styled.div`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	flex: 1 3 100%;
	flex-wrap: wrap;
	gap: 10px;

	&.action {
		flex: 1 1 20%;
		justify-content: flex-end;
	}
`;
const LoadMoreBtn = styled.button`
	font-family: "Proxima Nova", sans-serif;
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 150%;
	display: flex;
	align-items: center;
	text-align: center;
	letter-spacing: 0.01em;
	text-decoration-line: underline;
	color: #ffffff;
	background: transparent;
	border: none;
	margin: 20px auto;
`;

const useLeagueUsers = () => {
	const i18n = useTranslation();
	const dispatch = useDispatch();
	const isMobile = useIsMobile();
	const isRequested = useSelector(isLeagueRequested);
	const currentLeague = useSelector(getCurrentLeague);
	const leagueId = get(currentLeague, "id", 0);
	const {users, nextPage} = useSelector(getLeagueUsers);
	const userID = useSelector(getCurrentUserId);
	const isCommissioner = eq(currentLeague?.userId, userID);
	const [page, setPage] = useState<number>(0);

	useEffect(() => {
		if (leagueId) {
			dispatch(fetchLeagueUsers({leagueId}));
		}

		// return () => {
		// 	dispatch(clearLeagueUsers());
		// };
	}, [dispatch, leagueId]);

	const onLoadMore = () => {
		const nextPage = page + 1;
		dispatch(fetchLeagueUsers({leagueId, page: nextPage}));
		setPage(nextPage);
	};

	return {
		i18n,
		dispatch,
		isRequested,
		isMobile,
		users,
		nextPage,
		isCommissioner,
		onLoadMore,
	};
};

const useUserData = (user: ILeagueUser) => {
	const i18n = useTranslation();
	const dispatch = useDispatch();
	const currentLeague = useSelector(getCurrentLeague);
	const leagueId = get(currentLeague, "id", 0);

	const onRemoveUser = () => {
		dispatch(
			removeLeagueUser({
				userId: user.id,
				leagueId,
			})
		);
	};

	const removeUserConfirm = () => {
		dispatch(
			showPromptModal({
				title: i18n.t("league_users.delete.modal_title", "Are you sure?"),
				description: i18n.t(
					"league_users.delete.modal_description",
					`You are deleting the user ${user.username}. This action cannot be reversed.`,
					{
						name: user.username,
					}
				),
				denyButton: {
					text: i18n.t("league_users.delete.cancel", "No, Cancel"),
				},
				confirmButton: {
					text: i18n.t("league_users.delete.confirm", "Yes, Delete"),
					onClick: onRemoveUser,
				},
			})
		);
	};

	return {
		i18n,
		removeUserConfirm,
	};
};

const UserTableHeader: React.FC = () => {
	const {i18n} = useLeagueUsers();

	return (
		<HeaderRow>
			<Cell>{i18n.t("update_league.users.display_name", "Display Name")}</Cell>
			<Cell className={"action"}>{i18n.t("update_league.users.remove", "Remove")}</Cell>
		</HeaderRow>
	);
};

interface IRowProps {
	user: ILeagueUser;
}

const UserTableRow: React.FC<IRowProps> = ({user}) => {
	const {removeUserConfirm} = useUserData(user);
	const userID = useSelector(getCurrentUserId);
	const isCommissioner = eq(userID, user.id);
	const className = isCommissioner ? "commissioner" : "";

	return (
		<Row className={className}>
			<Cell>
				<Rank>
					{/*<Avatar levelColor={user.levelColor}>*/}
					{/*	/!*<BadgePosition>*!/*/}
					{/*	/!*	<Badge position={user.rank} isOwn={userId === userRank.userId} />*!/*/}
					{/*	/!*</BadgePosition>*!/*/}
					{/*	<img src={user.avatar || defaultAvatar} alt={`${user.username}'s avatar`} />*/}
					{/*</Avatar>*/}
				</Rank>
				<DisplayName>
					<p>{user.username}</p>
					{/*<p>{user.levelName}</p>*/}
				</DisplayName>
			</Cell>
			<Cell className={"action"}>
				{!isCommissioner && (
					<Button onClick={removeUserConfirm}>
						<IconClose />
					</Button>
				)}
			</Cell>
		</Row>
	);
};

export const LeagueUsers: React.FC = () => {
	const {i18n, isRequested, users, nextPage, onLoadMore} = useLeagueUsers();

	return (
		<Wrapper>
			<Container>
				{users ? (
					<UsersTable>
						<UserTableHeader />
						{users.map((user, index) => (
							<UserTableRow key={index} user={user} />
						))}
					</UsersTable>
				) : (
					<NoAvailableUsers>
						{i18n.t("update_league.settings.no_users", "No Users")}
					</NoAvailableUsers>
				)}
			</Container>

			{nextPage && (
				<LoadMoreBtn onClick={onLoadMore}>
					{i18n.t("update_league.settings.load_more_btn", "Load More")}
				</LoadMoreBtn>
			)}
			<MiddleLoading when={isRequested} />
		</Wrapper>
	);
};
